import React, { useEffect } from 'react';
import IEvaluationFormStep from 'interfaces/IEvaluationFormStep';
import { Alert, Button, Form, FormControl, FormGroup, Row, Stack } from 'react-bootstrap';
import ISimpleNameValuePair from 'interfaces/ISimpleNameValuePair';
import { useReviewFormValidator } from '../hooks/useReviewFormValidator';
import HelpButton from '../../../Form/HelpButton';
import ModalButton from '../../../ModalButton';
import { ReviewStatus } from '../../../../graphql/__generated__/graphql';

function generateReaccreditationCheckboxes(applicantName: string): ISimpleNameValuePair[] {
    return [
        {
            name: `I attest that this is an honest and valid evaluation of ${applicantName}`,
            value: false,
        },
        {
            name: `I confirm that I have seen the applicant's certificates and confirm the entries on their application are correct and a true reflection of the CPD undertaken`,
            value: false,
        },
        {
            name: `I understand that if for any reason information is forthcoming to suggest that this is not the case that I and/or ${applicantName} may forfeit their accreditation and possibly Association membership.`,
            value: false,
        },
    ];
}

function generateAccreditationCheckboxes(
    applicantName: string,
    minimumHours: number
): ISimpleNameValuePair[] {
    return [
        {
            name: `I confirm that ${applicantName} has completed a minimum of ${minimumHours} Hours Clinical Supervision/Consultation.`,
            value: false,
        },
        {
            name: `I confirm that I have personally supervised the work of ${applicantName} as outlined in this reference and
    competency framework document in relation to each of the cases listed.`,
            value: false,
        },
        {
            name: `I confirm that ${applicantName} has conformed to the level of attainment as stated and prescribed by the Association.`,
            value: false,
        },
        {
            name: `I attest that this is an honest and valid evaluation of ${applicantName}'s competencies.`,
            value: false,
        },
        {
            name: `I confirm that I have seen the applicant’s certificates and confirm that entries on their application are correct and a true reflection.`,
            value: false,
        },
        {
            name: `I understand that if for any reason information is forthcoming to suggest that the above conditions were not met
    the consultant and/or applicant may forfeit their accreditation and possibly Association membership.`,
            value: false,
        },
    ];
}

const Confirmation: React.FC<IEvaluationFormStep> = ({
    models,
    handleInputChangeEvent,
    handleSubmit,
    reviewFormat,
}) => {
    const [checkboxes, setCheckboxes] = React.useState<ISimpleNameValuePair[]>([]);

    const validator = useReviewFormValidator(models.requirements, reviewFormat);

    const applicantName =
        models.applicationReview.accreditationApplication?.member?.firstname +
        ' ' +
        models.applicationReview.accreditationApplication?.member?.lastname;

    const handleCheckboxClick = (index: number, value: boolean) => {
        setCheckboxes((prevState) => {
            return prevState.map((c, i) => {
                return {
                    ...c,
                    value: i === index ? value : c.value,
                };
            });
        });
    };

    const handleReturnToApplicant = () => {
        handleSubmit(ReviewStatus.WaitingOnApplicant);
    };

    useEffect(() => {
        if (models.applicationReview.isRenewal) {
            setCheckboxes(generateReaccreditationCheckboxes(applicantName));
        } else {
            const hoursOfSupervisionReq = models.requirements.experience.find(
                (req: any) => req.name === 'hours-of-supervision'
            );
            setCheckboxes(
                generateAccreditationCheckboxes(applicantName, hoursOfSupervisionReq?.min ?? 1)
            );
        }
    }, []);

    const errors = validator.validateForm(models.formData);
    const isValid = errors.length === 0;

    return (
        <Row className={'col-8'}>
            <>
                <h2>
                    Confirmation
                    <HelpButton
                        title={'Confirmation'}
                        content={process.env.REACT_APP_HELP_CONFIRMATION}
                    />
                </h2>
                {!isValid && (
                    <Alert variant={'warning'}>
                        <Alert.Heading>
                            Your evaluation contains one or more issues that need addressing before
                            you can submit approval.
                        </Alert.Heading>
                        <ul className={'list'}>
                            {errors.map((error, index) => (
                                <li key={index}>{error}</li>
                            ))}
                        </ul>
                        <p>
                            You can still return the form to the applicant for some adjustments by
                            clicking below and providing comments in the popup form.
                        </p>
                    </Alert>
                )}

                <FormGroup>
                    {isValid && (
                        <>
                            {checkboxes.map(({ name, value }, index) => (
                                <Row key={'confirm-' + index}>
                                    <Form.Label className={'d-flex'}>
                                        <Form.Check
                                            style={{ paddingRight: '1em' }}
                                            required
                                            onChange={(e) =>
                                                handleCheckboxClick(index, e.currentTarget.checked)
                                            }
                                            checked={value as boolean}
                                        />
                                        {name}
                                    </Form.Label>
                                </Row>
                            ))}
                        </>
                    )}
                    <Row className={'mt-2'}>
                        <Stack direction="horizontal" gap={2}>
                            {isValid && (
                                <Button
                                    variant={'secondary'}
                                    disabled={checkboxes.some((c) => !c.value)}
                                    onClick={() => handleSubmit(ReviewStatus.Complete)}
                                >
                                    Submit supervisor evaluation
                                </Button>
                            )}
                            <ModalButton
                                modalTitle={'Return for edits'}
                                buttonText={'Return for edits'}
                                onConfirmModalBtn={handleReturnToApplicant}
                                confirmBtnText={'Return for edits'}
                                buttonProps={{
                                    variant: 'warning',
                                }}
                                modalProps={{
                                    size: 'xl',
                                }}
                            >
                                <p>
                                    Please provide comments for the applicant citing the reasons for
                                    returning their application.
                                </p>
                                <FormControl
                                    as={'textarea'}
                                    rows={8}
                                    name={'comments'}
                                    value={models.formData.comments || ''}
                                    onChange={handleInputChangeEvent}
                                />
                            </ModalButton>
                        </Stack>
                    </Row>
                </FormGroup>
            </>
        </Row>
    );
};

export default Confirmation;
